import React from 'react';
import { CurriculumLibraryTreeView } from './CurriculumLibraryTreeView';
import { StudentCurriculumTreeView } from './StudentCurriculumTreeView';
import { AddTopicsTreeActions } from './AddTopicsTreeActions';
import { CreateUnitTreeButton } from './CreateUnitTreeButton';
import { viewStates } from '../constants';
import { buildLibraryTreeViewProps, buildStudentCurriculumTreeViewProps } from '../utils/treeViewUtils';
import { Label } from '../../../ui/label';

interface CreateTabMediumLayoutProps {
    selectedTopics: any[];
    expandedNodes: Set<string>;
    setExpandedNodes: (nodes: Set<string>) => void;
    curriculumData: any;
    tenantCustomCurriculumLibraryData?: any | null;
    isStudentCurriculumActive: boolean;
    showAddCustomMenu: boolean;
    onOpenCreateUnitWizard?: () => void;
    studentCurriculumData: any;
    setStudentCurriculumData?: (data: any) => void;
    selectedProvider: string;
    backendLessons: any[];
    selectedStudentPassFailThreshold: number | null;
    loadedNodeIds: Set<string>;
    loadingNodeIds: Set<string>;
    mediumTreeviewRef: React.RefObject<HTMLDivElement>;
    onTreeNodeSelect: (node: any) => void;
    onLoadNodeChildren: (nodeId: string, nodeType: 'year' | 'topic') => void;
    onCreateAssessment: () => void;
    onOpenCreateYourOwnWizard: () => void;
    onRemoveTopicsFromStudentCurriculum: () => void;
    onAddTopics: () => void;
    onDeleteTenantCustomCurriculumTopics?: () => void;
    setViewState: (state: number) => void;
    studentId?: string | null;
    selectedStudentFirstName?: string;
    onOpenUnitFromPlan?: (unit: any) => void;
}

export function CreateTabMediumLayout({
    selectedTopics,
    expandedNodes,
    setExpandedNodes,
    curriculumData,
    tenantCustomCurriculumLibraryData,
    isStudentCurriculumActive,
    showAddCustomMenu,
    onOpenCreateUnitWizard,
    studentCurriculumData,
    setStudentCurriculumData,
    selectedProvider,
    backendLessons,
    selectedStudentPassFailThreshold,
    loadedNodeIds,
    loadingNodeIds,
    mediumTreeviewRef,
    onTreeNodeSelect,
    onLoadNodeChildren,
    onCreateAssessment,
    onOpenCreateYourOwnWizard,
    onRemoveTopicsFromStudentCurriculum,
    onAddTopics,
    onDeleteTenantCustomCurriculumTopics,
    setViewState,
    studentId,
    selectedStudentFirstName = '',
    onOpenUnitFromPlan
}: CreateTabMediumLayoutProps) {
    const hasSelectedTopics = selectedTopics.length > 0;

    const libraryTreeViewProps = buildLibraryTreeViewProps(
        onTreeNodeSelect,
        selectedTopics,
        expandedNodes,
        setExpandedNodes,
        curriculumData,
        studentCurriculumData,
        selectedProvider,
        backendLessons,
        selectedStudentPassFailThreshold,
        onLoadNodeChildren,
        loadedNodeIds,
        loadingNodeIds,
        !hasSelectedTopics && curriculumData, // Show hint when no topics selected and data available
        false,
        selectedStudentFirstName,
        studentId ?? null,
        onOpenUnitFromPlan
    );

    const customCurriculumTreeProps =
        tenantCustomCurriculumLibraryData?.e179_Subjects?.length > 0
            ? buildLibraryTreeViewProps(
                  onTreeNodeSelect,
                  selectedTopics,
                  expandedNodes,
                  setExpandedNodes,
                  tenantCustomCurriculumLibraryData,
                  studentCurriculumData,
                  'custom',
                  backendLessons,
                  selectedStudentPassFailThreshold,
                  onLoadNodeChildren,
                  loadedNodeIds,
                  loadingNodeIds,
                  false,
                  true,
                  selectedStudentFirstName,
                  studentId ?? null,
                  onOpenUnitFromPlan
              )
            : null;

    const studentCurriculumTreeViewProps = buildStudentCurriculumTreeViewProps(
        onTreeNodeSelect,
        selectedTopics,
        expandedNodes,
        setExpandedNodes,
        studentCurriculumData,
        backendLessons,
        selectedStudentPassFailThreshold,
        onLoadNodeChildren,
        loadedNodeIds,
        loadingNodeIds,
        studentId,
        selectedStudentFirstName,
        onOpenUnitFromPlan,
        setStudentCurriculumData
    );

    return (
        <div ref={mediumTreeviewRef} className="hidden sm:block lg:hidden relative min-h-full">
            <div className="min-h-full">
                <div className="medium-treeview-wrapper">
                    {isStudentCurriculumActive ? (
                        <div className="flex flex-col">
                            {onOpenCreateUnitWizard && (
                                <div className="px-2 pb-2 pt-2 mb-3 flex-shrink-0">
                                    <CreateUnitTreeButton
                                        onOpenCreateUnitWizard={onOpenCreateUnitWizard}
                                        selectedTopics={selectedTopics}
                                        studentCurriculumData={studentCurriculumData}
                                        onCreateAssessment={() => {
                                            onCreateAssessment();
                                            setViewState(viewStates.creatingAssessment);
                                        }}
                                        onRemoveTopics={onRemoveTopicsFromStudentCurriculum}
                                    />
                                </div>
                            )}
                            <StudentCurriculumTreeView {...studentCurriculumTreeViewProps} className="" />
                        </div>
                    ) : (
                        <div className="flex flex-col">
                            {showAddCustomMenu && (
                            <div className="px-2 pb-2 pt-2 mb-3 flex-shrink-0">
                                <AddTopicsTreeActions
                                    onOpenCreateYourOwnWizard={onOpenCreateYourOwnWizard}
                                    selectedTopics={selectedTopics}
                                    onAddTopics={onAddTopics}
                                    onDeleteTenantCustomCurriculumTopics={onDeleteTenantCustomCurriculumTopics}
                                />
                            </div>
                            )}
                            {customCurriculumTreeProps && (
                                <div className="flex flex-col mb-2">
                                    <Label className="block px-2 pb-1 text-lg font-semibold text-[#1a5172]">
                                        Custom curriculum
                                    </Label>
                                    <CurriculumLibraryTreeView {...customCurriculumTreeProps} className="" />
                                </div>
                            )}
                            <div className="flex flex-col">
                                <Label className="block px-2 pb-1 text-lg font-semibold text-[#1a5172]">
                                    National Curriculum
                                </Label>
                                <CurriculumLibraryTreeView {...libraryTreeViewProps} className="" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
